import { useState } from 'react';
import { RequirePermission } from '../components/NoAccess';
import { Eye, Pencil, RotateCcw, Send, UserPlus, UserX } from 'lucide-react';
import type {
  ConsoleUserRow, CursorPage, GrantableRole, Plant, UserStatus,
} from '@dru/types';
import { USER_STATUS_LABEL } from '@dru/types';
import {
  Alert, IconAction, Button, DataTable, SearchInput,
  StatusBadge, TextField, type Tone, cn, useToast,
} from '@dru/ui';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { formatDate, formatDateTime } from '../../../lib/format';
import { usePages } from '../../../lib/usePages';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { Dialog } from '../../../components/Dialog';
import { PlantAccessField } from '../../../components/PlantAccessField';
import { ALL_PLANTS, initialPlantAccess, plantAccessMode, plantAccessReady, scopeIdsFor } from '../../../lib/plant-access';

/** A role that may be limited to a plant starts at the whole company; narrowing it is the deliberate step. */
function startAccess(role: GrantableRole | undefined, plantIds: string[]): string {
  const mode = plantAccessMode(role);
  const v = initialPlantAccess(mode, plantIds);
  return mode === 'optional' && !v ? ALL_PLANTS : v;
}


/**
 * The people who use the consoles.
 *
 * Not the workforce — a worker's PWA account is not a console login and is
 * deliberately absent from this list. These are the colleagues who run
 * campaigns, import employees and read compliance.
 *
 * Two rules shape the screen. A new account is INVITED with no password, so
 * creating one never issues a credential; and the role picker offers only what
 * this administrator already holds, because otherwise "add a user" is a way to
 * acquire permissions nobody granted you.
 */
/**
 * The reaches a company's own roles are defined at.
 *
 * By scope rather than by key: a company defines roles of its own — "Regional
 * EHS", "Site nurse" — under keys nobody can list in advance, and each of
 * those is a colleague; a venue's, the panel's or the platform's role is
 * PARTNER, SELF or GLOBAL whatever it is called.
 */
const COMPANY_ROLE_SCOPES = new Set(['TENANT', 'PLANT', 'CAMP']);

function UsersPage() {
  const { principal, permissions } = useAuth();
  const canAdd = holds(permissions, 'user.create') && holds(permissions, 'user.role-assign');
  const canInvite = holds(permissions, 'user.create');
  const canSuspend = holds(permissions, 'user.suspend');
  const canEditName = holds(permissions, 'user.create');
  const canEditAccess = holds(permissions, 'user.role-assign');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const users = usePages<ConsoleUserRow>((cursor) => api.get<CursorPage<ConsoleUserRow>>(`/users?limit=50${cursor ? `&cursor=${cursor}` : ''}`), []);
  // Names for the plant ids a role is bound to: "3 plant" said how many, not which.
  const plants = useQuery<CursorPage<Plant>>(() => api.getAllPlants('/plants?limit=100'), []);
  const plantName = new Map((plants.data?.items ?? []).map((p) => [p.id, p.name]));
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [asking, setAsking] = useState<{ user: ConsoleUserRow; act: 'suspend' | 'reinstate' } | null>(null);
  const [viewing, setViewing] = useState<ConsoleUserRow | null>(null);
  const [editing, setEditing] = useState<ConsoleUserRow | null>(null);

  // Two kinds of account sit under a company, and only one of them is the
  // company's. A colleague holds a company role. A hospital coordinator, a
  // laboratory technician or a certifying surgeon created here belongs to a
  // venue or to the panel: they were put under a company before venues had
  // logins of their own, and the company neither hires nor dismisses them.
  // Listing them beside HR, each with a Suspend button, invited a company to
  // switch off a hospital's pathologist.
  // Decided by each role's defined reach (the server's rule, identity/
  // company-account.ts), not by a list of role keys that a new role escapes.
  const isCompanyAccount = (u: ConsoleUserRow) =>
    u.roles.every((r) => COMPANY_ROLE_SCOPES.has(r.roleScope ?? r.scopeType));
  const matches = (u: ConsoleUserRow) => !q
    || u.fullName.toLowerCase().includes(q.toLowerCase())
    || u.email.toLowerCase().includes(q.toLowerCase());
  const companyUsers = users.items.filter(isCompanyAccount);
  const shown = companyUsers.filter((u) => matches(u) && (!status || u.status === status));
  // Counts only once every page is in: a count over the first 50 would read
  // as the company's total.
  const complete = !users.loading && !users.hasMore;
  const countOf = (st: string) => (complete ? companyUsers.filter((u) => !st || u.status === st).length : null);
  // The server refuses to suspend or demote the last active Company Admin.
  // Said on the row, from the full list only, instead of as a refusal.
  const activeAdmins = companyUsers.filter((u) => u.status === 'ACTIVE' && u.roles.some((r) => r.key === 'HR_ADMIN'));
  const isLastAdmin = (u: ConsoleUserRow) => complete && activeAdmins.length === 1 && activeAdmins[0]!.id === u.id;

  /** What a person can see: the whole company, or named plants only. */
  const reach = (u: ConsoleUserRow) => {
    const bound = u.roles.filter((r) => r.scopeType === 'PLANT' && r.scopeIds.length > 0);
    if (!u.roles.length) return <span className="text-small text-neutral-500">No access</span>;
    if (!bound.length) return <span className="text-small text-neutral-800">All plants</span>;
    const ids = [...new Set(bound.flatMap((r) => r.scopeIds))];
    return (
      <span className="text-small text-neutral-800">
        {ids.map((id) => plantName.get(id) ?? 'Unknown plant').join(', ')}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users"
        hint="Colleagues who sign in to run your company's health programme. Employees use the employee app and are not listed here, nor are lab, hospital or physician accounts."
        actions={canAdd && <Button icon={<UserPlus className="h-4 w-4" />} onClick={() => setAdding(true)}>Add user</Button>}
      />

      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={q}
          onValueChange={setQ}
          placeholder="Search name or email"
          aria-label="Search users"
          className="w-full sm:w-72"
        />
        <div role="group" aria-label="Show users by status"
             className="flex gap-2 max-md:-mx-4 max-md:w-[calc(100%+2rem)] max-md:overflow-x-auto max-md:px-4 max-md:pb-1">
          {STATUS_FILTERS.map((f) => {
            const n = countOf(f.value);
            const on = status === f.value;
            return (
              <button key={f.value || 'all'} type="button" aria-pressed={on} onClick={() => setStatus(f.value)}
                className={cn(
                  'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-small font-medium transition-colors duration-hover max-md:h-10',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  on ? 'border-primary-600 bg-primary-600 text-white' : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50',
                )}>
                {f.label}
                {n != null && <span className={cn('tabular-nums', on ? 'text-white/80' : 'text-neutral-500')}>{n}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <DataTable<ConsoleUserRow>
        rows={shown}
        loading={users.loading}
        error={users.error}
        onRetry={users.reload}
        hasMore={users.hasMore}
        onLoadMore={users.loadMore}
        loadingMore={users.loadingMore}
        rowKey={(u) => u.id}
        empty={q || status ? {
          title: 'Nobody matches',
          hint: 'Clear the search or choose All to see every user.',
          action: <Button variant="secondary" onClick={() => { setQ(''); setStatus(''); }}>Clear filters</Button>,
        } : {
          title: 'No colleagues added yet',
          hint: 'Invite the people who will add employees, book checkups or pay invoices.',
          action: canAdd ? <Button icon={<UserPlus className="h-4 w-4" />} onClick={() => setAdding(true)}>Add user</Button> : undefined,
        }}
        columns={[
          {
            key: 'person', header: 'Name', alwaysVisible: true,
            cell: (u) => (
              <div className="min-w-0">
                <div className="font-medium text-neutral-900">
                  {u.fullName}
                  {u.id === principal?.id && <span className="ml-1.5 text-caption font-normal text-neutral-500">(you)</span>}
                </div>
                <div className="break-all text-caption text-neutral-500">{u.email}</div>
              </div>
            ),
          },
          {
            key: 'roles', header: 'Role',
            cell: (u) => (
              u.roles.length
                ? <span className="text-small text-neutral-800">{u.roles.map((r) => r.name).join(', ')}</span>
                : <span className="text-small text-neutral-500">No role</span>
            ),
          },
          { key: 'reach', header: 'Plant access', cell: reach },
          {
            key: 'status', header: 'Status',
            cell: (u) => (
              <div>
                <StatusBadge tone={userTone(u.status)}>{statusLabel(u.status)}</StatusBadge>
                {u.status === 'INVITED' && (
                  <div className="mt-0.5 text-caption text-neutral-500">Has not set a password yet</div>
                )}
                {u.status === 'SUSPENDED' && u.suspendedReason && (
                  <div className="mt-0.5 max-w-[16rem] text-caption text-neutral-500">{u.suspendedReason}</div>
                )}
              </div>
            ),
          },
          {
            key: 'last', header: 'Last login', align: 'right', hideOnMobile: true,
            cell: (u) => (
              <span className="text-small tabular-nums text-neutral-600">
                {u.lastLoginAt ? formatDate(u.lastLoginAt) : 'Never'}
              </span>
            ),
          },
          {
            key: 'act', header: '', align: 'right',
            cell: (u) => {
              const self = u.id === principal?.id;
              const lastAdmin = isLastAdmin(u);
              return (
                <div className="flex items-center justify-end gap-0.5 [@media(pointer:coarse)]:gap-2">
                  {u.status === 'INVITED' && canInvite && (
                    <SendInvitation user={u} onSent={(email, expiresAt) => toast('success', `Invitation sent again to ${email}. The new link works until ${formatDateTime(expiresAt)}; the old one no longer does.`)} />
                  )}
                  <IconAction label={`View ${u.fullName}`} icon={Eye} onClick={() => setViewing(u)} />
                  {(canEditName || canEditAccess) && (
                    <IconAction label={self ? 'Edit your name (your own access is changed by another admin)' : `Edit access for ${u.fullName}`}
                                icon={Pencil} onClick={() => setEditing(u)} />
                  )}
                  {/* Suspend, never delete: an account's audit history must
                      stay, and suspending ends its sessions. The server refuses
                      your own account and the last Company Admin; both are
                      shown disabled with the reason as the label. */}
                  {canSuspend && u.status === 'ACTIVE' && (
                    <IconAction
                      label={self ? 'You cannot suspend your own account'
                        : lastAdmin ? `${u.fullName} is the only Company Admin and cannot be suspended`
                          : `Suspend ${u.fullName}`}
                      icon={UserX} danger disabled={self || lastAdmin}
                      onClick={() => setAsking({ user: u, act: 'suspend' })} />
                  )}
                  {canSuspend && u.status === 'SUSPENDED' && (
                    <IconAction label={`Reinstate ${u.fullName}`} icon={RotateCcw} onClick={() => setAsking({ user: u, act: 'reinstate' })} />
                  )}
                </div>
              );
            },
          },
        ]}
      />

      {/* Venue and panel accounts are not shown here (owner, 26 Oct 2026): they
          are not the company's colleagues and cannot be managed from it. */}

      {adding && (
        <AddDialog
          onClose={() => setAdding(false)}
          onDone={(sent) => {
            setAdding(false); users.reload();
            toast(sent ? 'success' : 'warning', sent
              ? 'Invitation sent. They choose their own password from the emailed link, which works for 72 hours.'
              : 'User added, but the invitation email could not be sent. Use “Resend invitation” on their row.');
          }}
        />
      )}

      {viewing && (
        <ViewUserDialog user={viewing} reach={reach(viewing)} onClose={() => setViewing(null)} />
      )}
      {editing && (
        <EditUserDialog
          user={editing}
          self={editing.id === principal?.id}
          canEditName={canEditName}
          canEditAccess={canEditAccess}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); users.reload(); toast('success', 'Saved.'); }}
        />
      )}
      {asking && (
        <AccountDialog
          user={asking.user}
          act={asking.act}
          onClose={() => setAsking(null)}
          onDone={() => { setAsking(null); users.reload(); }}
        />
      )}
    </div>
  );
}

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INVITED', label: 'Invited' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

const statusLabel = (s: UserStatus) => (s === 'INVITED' ? 'Invited' : USER_STATUS_LABEL[s]);

function userTone(s: UserStatus): Tone {
  switch (s) {
    case 'ACTIVE': return 'covered';
    case 'INVITED': return 'dueSoon';
    case 'SUSPENDED': return 'overdue';
    default: return 'neutral';
  }
}

/** Everything about one account, and what has happened to it. */
function ViewUserDialog({ user, reach, onClose }: { user: ConsoleUserRow; reach: React.ReactNode; onClose: () => void }) {
  const history = useQuery<Array<{ id: string; fromStatus: string; toStatus: string; reason: string; occurredAt: string }>>(
    () => api.get(`/admin/users/${user.id}/status-history`), [user.id]);
  const row = (label: string, value: React.ReactNode) => (
    <div className="grid grid-cols-[9rem_1fr] gap-2 py-1.5"><dt className="text-caption text-neutral-500">{label}</dt><dd className="text-small text-neutral-900">{value}</dd></div>
  );
  return (
    <Dialog title={user.fullName} onClose={onClose}>
      <dl className="mt-2 divide-y divide-neutral-100">
        {row('Email', <span className="break-all">{user.email}</span>)}
        {row('Role', user.roles.map((r) => r.name).join(', ') || 'No role')}
        {row('Can see', reach)}
        {row('State', user.status === 'INVITED' ? 'Invited' : USER_STATUS_LABEL[user.status])}
        {user.suspendedReason && row('Reason', user.suspendedReason)}
        {row('Last signed in', user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never')}
        {row('Added', formatDate(user.createdAt))}
      </dl>
      <h3 className="mt-4 text-small font-semibold text-neutral-800">Account history</h3>
      {history.loading ? <p className="text-caption text-neutral-500">Loading…</p>
        : history.data?.length ? (
          <ul className="mt-1 space-y-1">
            {history.data.map((h) => (
              <li key={h.id} className="text-caption text-neutral-700">
                {formatDateTime(h.occurredAt)} · {h.fromStatus.toLowerCase()} → {h.toStatus.toLowerCase()} · {h.reason}
              </li>
            ))}
          </ul>
        ) : <p className="text-caption text-neutral-500">No suspensions or reinstatements.</p>}
      <div className="mt-5 flex justify-end"><Button variant="secondary" onClick={onClose}>Close</Button></div>
    </Dialog>
  );
}

/**
 * Name, and — for a login that may assign roles — role and plant access.
 * The server applies the same rules as adding a user, and refuses changing
 * your own access or demoting the last Company Admin.
 */
function EditUserDialog({ user, self, canEditName, canEditAccess, onClose, onDone }: {
  user: ConsoleUserRow; self: boolean; canEditName: boolean; canEditAccess: boolean;
  onClose: () => void; onDone: () => void;
}) {
  const accessEditable = canEditAccess && !self;
  const roles = useQuery<CursorPage<GrantableRole> | null>(
    () => (accessEditable ? api.get('/users/roles') : Promise.resolve(null)), [accessEditable]);
  const plants = useQuery<CursorPage<Plant>>(() => api.getAllPlants('/plants?limit=100'), []);
  const plantList = plants.data?.items ?? [];
  const current = user.roles[0];
  const [fullName, setFullName] = useState(user.fullName);
  const [roleKey, setRoleKey] = useState(current?.key ?? '');
  const offered = roles.data?.items ?? [];
  const role = offered.find((r) => r.key === roleKey);
  const mode = plantAccessMode(role);
  const [plantAccess, setPlantAccess] = useState(() => {
    const ids = current?.scopeType === 'PLANT' ? current.scopeIds : [];
    return ids.length === 1 ? ids[0]! : initialPlantAccess(plantAccessMode(current ? { key: current.key, scopeType: current.roleScope } : undefined), ids);
  });
  const scopeIds = scopeIdsFor(mode, plantAccess);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const roleKnown = !accessEditable || !!role;
  const ready = fullName.trim().length >= 2 && roleKnown && (!accessEditable || plantAccessReady(mode, plantAccess));

  async function save() {
    setBusy(true); setError(null);
    try {
      const body: Record<string, unknown> = {};
      if (canEditName && fullName.trim() !== user.fullName) body.fullName = fullName.trim();
      if (accessEditable && role) {
        const before = current?.scopeType === 'PLANT' ? current.scopeIds : [];
        const changed = role.key !== current?.key || before.join(',') !== scopeIds.join(',');
        if (changed) { body.roleKey = role.key; body.scopeIds = scopeIds; }
      }
      if (Object.keys(body).length === 0) { onClose(); return; }
      await api.patch(`/users/${user.id}`, body);
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog title={self ? 'Edit your name' : `Edit access for ${user.fullName}`} onClose={onClose}>
      <div className="mt-3 space-y-3">
        <TextField label="Full name" required value={fullName} onChange={setFullName} disabled={!canEditName} />
        <TextField label="Work email" value={user.email} onChange={() => {}} disabled
          hint="The sign-in email cannot be changed. Add a new user for a new address." />
        {accessEditable ? (
          <>
            <RolePicker
              roles={offered}
              loading={roles.loading}
              error={roles.error}
              value={roleKey}
              // A role this admin cannot grant stays readable, and unchangeable.
              locked={current && !offered.some((r) => r.key === current.key) ? current : undefined}
              onChange={(v) => {
                setRoleKey(v);
                const next = offered.find((r) => r.key === v);
                setPlantAccess(startAccess(next, plantList.map((p) => p.id)));
              }}
            />
            <PlantAccessField mode={mode} plants={plantList} value={plantAccess} onChange={setPlantAccess} />
            <p className="text-caption text-neutral-500">Changing the role or plant signs this person out, so the new access applies at once.</p>
          </>
        ) : (
          <p className="text-caption text-neutral-500">
            {self ? 'You cannot change your own role or plant access.' : 'Changing the role needs the right to assign roles.'}
          </p>
        )}
      </div>
      {error && <Alert>{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} loading={busy} disabled={!ready}>Save</Button>
      </div>
    </Dialog>
  );
}

function AccountDialog({ user, act, onClose, onDone }: {
  user: ConsoleUserRow;
  act: 'suspend' | 'reinstate';
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    setBusy(true); setError(null);
    try {
      await api.post(`/admin/users/${user.id}/${act}`, { reason: reason.trim() });
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  const title = act === 'suspend' ? `Suspend ${user.fullName}` : `Reinstate ${user.fullName}`;

  return (
    <Dialog title={title} onClose={onClose}>
      {act === 'suspend' && (
        <Alert tone="warning" className="mt-1">
          Blocks sign-in and ends every session they have open right now. Nothing is deleted;
          reinstating restores the account as it was.
        </Alert>
      )}
      {act === 'reinstate' && (
        <p className="mt-1 text-sm text-neutral-700">
          Sign-in works again with the password they already have. If they have forgotten it,
          they can ask for a reset link from the sign-in screen.
        </p>
      )}
      <TextField className="mt-3" label="Reason" value={reason} onChange={setReason} required autoFocus
        hint="At least ten characters."
        info="Recorded in the audit trail."
        placeholder={act === 'suspend' ? 'Left the company on 30 September' : 'Returned from leave'} />
      {error && <Alert>{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant={act === 'suspend' ? 'danger' : 'primary'} onClick={go} loading={busy}
          disabled={reason.trim().length < 10}>
          {act === 'suspend' ? 'Suspend' : 'Reinstate'}
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * Sends the invitation email, or sends it again. The same route does both,
 * and a resend kills the previous link, so only one ever works at a time.
 * Nothing about the link comes back to this screen.
 */
function SendInvitation({ user, onSent }: {
  user: ConsoleUserRow;
  onSent: (email: string, expiresAt: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function go() {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ sent: true; expiresAt: string; email: string }>(`/users/${user.id}/invite`);
      onSent(r.email, r.expiresAt);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <IconAction
        label={busy ? `Sending invitation to ${user.fullName}` : `Resend invitation to ${user.fullName}`}
        icon={Send} primary loading={busy} onClick={go}
      />
      {error && <span role="alert" className="text-caption text-danger-600">{error}</span>}
    </div>
  );
}

function AddDialog({ onClose, onDone }: { onClose: () => void; onDone: (sent: boolean) => void }) {
  const roles = useQuery<CursorPage<GrantableRole>>(() => api.get('/users/roles'), []);
  const plants = useQuery<CursorPage<Plant>>(() => api.getAllPlants('/plants?limit=100'), []);
  const plantList = plants.data?.items ?? [];
  const [form, setForm] = useState({ email: '', fullName: '', roleKey: '' });
  const [plantAccess, setPlantAccess] = useState('');
  // Only roles this admin may grant; the server refuses anything else.
  const offered = roles.data?.items ?? [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Idempotent create: a double click or a retry after a lost response
  // returns the same account instead of a second one.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const role = offered.find((r) => r.key === form.roleKey);
  const mode = plantAccessMode(role);
  const scopeIds = scopeIdsFor(mode, plantAccess);
  const email = form.email.trim();
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const nameOk = form.fullName.trim().length >= 2;
  const ready = emailOk && nameOk && !!role && plantAccessReady(mode, plantAccess);

  async function submit() {
    setBusy(true); setError(null);
    try {
      const created = await api.post<{ id: string }>('/users', {
        email: email.toLowerCase(),
        fullName: form.fullName.trim(),
        roleKey: form.roleKey,
        ...(scopeIds.length ? { scopeIds } : {}),
      }, idempotencyKey);
      // The account exists either way; the email is the part that can fail
      // (SMTP not set up, address suppressed). Say so rather than undoing
      // the account — the row offers "Resend invitation" for a second try.
      let sent = true;
      try { await api.post(`/users/${created.id}/invite`); } catch { sent = false; }
      onDone(sent);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog title="Add user" onClose={busy ? () => {} : onClose}>
      <p className="mt-1 text-small text-neutral-600">
        We email them a link to choose their own password. The account cannot sign in until they do.
      </p>

      <div className="mt-4 space-y-4">
        <TextField label="Full name" required value={form.fullName} autoFocus
          onChange={(v) => setForm({ ...form, fullName: v })} />
        <TextField label="Work email" required type="email" value={form.email}
          hint="The invitation goes here. It is also their sign-in, and cannot be changed later."
          error={email && !emailOk ? 'Enter a full email address, like name@company.com.' : undefined}
          onChange={(v) => setForm({ ...form, email: v })} />

        <RolePicker
          roles={offered}
          loading={roles.loading}
          error={roles.error}
          value={form.roleKey}
          onChange={(v) => {
            setForm({ ...form, roleKey: v });
            const next = offered.find((r) => r.key === v);
            setPlantAccess(startAccess(next, plantList.map((p) => p.id)));
          }}
        />

        <PlantAccessField mode={mode} plants={plantList} value={plantAccess} onChange={setPlantAccess} />
      </div>

      {error && <Alert>{error}</Alert>}

      <p className="mt-4 text-caption text-neutral-500">The link works once, for 72 hours. You can resend it from their row.</p>
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button icon={<Send className="h-4 w-4" />} onClick={submit} loading={busy} disabled={!ready}>Send invitation</Button>
      </div>
    </Dialog>
  );
}

/**
 * Plain-language reach of the company roles, for a role whose own
 * description is empty. Kept to what each role can do, not its permissions.
 */
const ROLE_PLAIN: Record<string, string> = {
  HR_ADMIN: 'Runs the whole company account: employees, checkups, reports, billing and users.',
  PLANT_HR: 'Employees, checkups and appointments for one plant.',
  PLANT_ACCOUNTS: 'Invoices and payments for one plant. No medical information.',
  EHS_VIEWER: 'Can look at employees, appointments, certificates and compliance. Cannot change anything.',
};

/**
 * The role as a short list of choices, each saying what it lets the person
 * do. Only grantable roles come from the server, so every choice works.
 */
function RolePicker({ roles, loading, error, value, onChange, locked }: {
  roles: GrantableRole[];
  loading: boolean;
  error?: string | null;
  value: string;
  onChange: (key: string) => void;
  locked?: { key: string; name: string };
}) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-small font-medium text-neutral-800">
        Role <span className="text-danger-600" aria-hidden>*</span>
      </legend>
      {error ? <Alert>{error}</Alert>
        : loading ? <p className="text-small text-neutral-500">Loading roles…</p>
          : (
            <div className="space-y-2">
              {locked && (
                <div className="rounded-btn border border-neutral-200 bg-neutral-50 px-3 py-2.5">
                  <div className="text-small font-medium text-neutral-700">{locked.name}</div>
                  <div className="text-caption text-neutral-500">Their current role. You cannot give this role, so you cannot change it either.</div>
                </div>
              )}
              {!locked && roles.map((r) => {
                const on = value === r.key;
                return (
                  <label key={r.key}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-btn border px-3 py-2.5 transition-colors duration-hover',
                      'focus-within:ring-2 focus-within:ring-primary-500',
                      on ? 'border-primary-600 bg-primary-50' : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50',
                    )}>
                    <input type="radio" name="role" value={r.key} checked={on} onChange={() => onChange(r.key)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-primary-600" />
                    <span className="min-w-0">
                      <span className="block text-small font-medium text-neutral-900">{r.name}</span>
                      <span className="block text-caption text-neutral-600">{r.description || ROLE_PLAIN[r.key] || 'A company role.'}</span>
                    </span>
                  </label>
                );
              })}
              {!locked && roles.length === 0 && (
                <p className="text-small text-neutral-500">There are no roles you can give. Ask your Company Admin.</p>
              )}
            </div>
          )}
    </fieldset>
  );
}

export function Users() {
  return <RequirePermission need="user.read" title="Users" what="Company users"><UsersPage /></RequirePermission>;
}
